import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { BlockerFunction, useBlocker } from 'react-router-dom';

import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

type NavigationBlockerOptions = {
  message: string;
  blocked: boolean;
  proceedText: string;
  cancelText: string;
};

interface BlockerContextType {
  navigationBlocker: NavigationBlockerOptions;
  setNavigationBlocker: (value: NavigationBlockerOptions) => void;
  setOnProceed: (fn: () => void) => void;
  setOnCancel: (fn: () => void) => void;
}

const initializedOptions = {
  blocked: false,
  cancelText: 'avbryt',
  proceedText: 'släng',
  message: 'Vill du slänga ändringarna?'
};

const BlockerContext = createContext<BlockerContextType | undefined>(undefined);
export const BlockerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setDialog, clearDialogStack } = useDialog();

  const [navigationBlocker, setNavigationBlocker] = useState<NavigationBlockerOptions>(initializedOptions);

  const [onProceed, setOnProceed] = useState<() => void>(() => () => {});
  const [onCancel, setOnCancel] = useState<() => void>(() => () => {});

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      navigationBlocker.blocked && currentLocation.pathname !== nextLocation.pathname,
    [navigationBlocker.blocked]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setDialog(RaaDialog, {
        cancelButtonText: navigationBlocker.cancelText,
        proceedButtonText: navigationBlocker.proceedText,
        header: navigationBlocker.message,
        proceed: () => {
          onProceed();
          blocker.proceed();
          blocker.reset();
          setNavigationBlocker(initializedOptions);
        },
        cancel: () => {
          onCancel();
          clearDialogStack();
        }
      });
    }
  }, [
    navigationBlocker.cancelText,
    navigationBlocker.proceedText,
    navigationBlocker.message,
    onCancel,
    onProceed,
    setDialog,
    clearDialogStack,
    blocker
  ]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !navigationBlocker.blocked) {
      clearDialogStack();
      blocker.reset();
    }
  }, [blocker, navigationBlocker.blocked, clearDialogStack]);

  return (
    <BlockerContext.Provider
      value={{
        navigationBlocker,
        setNavigationBlocker,
        setOnProceed,
        setOnCancel
      }}
    >
      {children}
    </BlockerContext.Provider>
  );
};

export const useBlockerContext = (): BlockerContextType => {
  const context = useContext(BlockerContext);
  if (!context) {
    throw new Error('useBlockerContext must be used within a BlockerProvider');
  }
  return context;
};
